/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { BaseService, IWrapHTTPConfig } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { EmptyResponse } from '../models/empty-response';
import { ProposalChatForm } from '../models/proposal-chat-form';
import { ProposalChatItemsForm } from '../models/proposal-chat-items-form';
import { ProposalChatSearchForm } from '../models/proposal-chat-search-form';
import { ResponseInteger } from '../models/response-integer';
import { ResponsePageableListDtoProposalChatListFinalDto } from '../models/response-pageable-list-dto-proposal-chat-list-final-dto';
import { ResponseProposalChatItemDto } from '../models/response-proposal-chat-item-dto';

@Injectable({
  providedIn: 'root',
})
export class AdminProposalApiService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation getProposalChats
   */
  static readonly GetProposalChatsPath = '/api/v1/admin/proposal/chat/{chatId}';

  /**
   * Get proposal chat
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getProposalChats()` instead.
   *
   * This method doesn't expect any request body.
   */
  private getProposalChats$Response(params: {
    chatId: number;
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<ResponseProposalChatItemDto>> {

    const rb = new RequestBuilder(this.rootUrl, AdminProposalApiService.GetProposalChatsPath, 'get');
    if (params) {
      rb.path('chatId', params.chatId, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ResponseProposalChatItemDto>;
      })
    );
  }

  /**
   * Get proposal chat
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getProposalChats$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getProposalChats(params: {
    chatId: number;
  }, config?: IWrapHTTPConfig
): Observable<ResponseProposalChatItemDto> {

    return this.wrapHTTPQuery(this.getProposalChats$Response(params, config).pipe(
      map((r: StrictHttpResponse<ResponseProposalChatItemDto>) => r.body as ResponseProposalChatItemDto)
    ), config);
  }

  /**
   * Path part for operation updateProposalChats
   */
  static readonly UpdateProposalChatsPath = '/api/v1/admin/proposal/chat/{chatId}';

  /**
   * Update proposal chat
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateProposalChats()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  private updateProposalChats$Response(params: {
    chatId: number;
    body: ProposalChatForm
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<ResponseProposalChatItemDto>> {

    const rb = new RequestBuilder(this.rootUrl, AdminProposalApiService.UpdateProposalChatsPath, 'put');
    if (params) {
      rb.path('chatId', params.chatId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ResponseProposalChatItemDto>;
      })
    );
  }

  /**
   * Update proposal chat
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `updateProposalChats$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateProposalChats(params: {
    chatId: number;
    body: ProposalChatForm
  }, config?: IWrapHTTPConfig
): Observable<ResponseProposalChatItemDto> {

    return this.wrapHTTPQuery(this.updateProposalChats$Response(params, config).pipe(
      map((r: StrictHttpResponse<ResponseProposalChatItemDto>) => r.body as ResponseProposalChatItemDto)
    ), config);
  }

  /**
   * Path part for operation unreadProposalChats
   */
  static readonly UnreadProposalChatsPath = '/api/v1/admin/proposal/unread';

  /**
   * Unread proposal chats
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `unreadProposalChats()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  private unreadProposalChats$Response(params: {
    body: ProposalChatItemsForm
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<EmptyResponse>> {

    const rb = new RequestBuilder(this.rootUrl, AdminProposalApiService.UnreadProposalChatsPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<EmptyResponse>;
      })
    );
  }

  /**
   * Unread proposal chats
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `unreadProposalChats$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  unreadProposalChats(params: {
    body: ProposalChatItemsForm
  }, config?: IWrapHTTPConfig
): Observable<EmptyResponse> {

    return this.wrapHTTPQuery(this.unreadProposalChats$Response(params, config).pipe(
      map((r: StrictHttpResponse<EmptyResponse>) => r.body as EmptyResponse)
    ), config);
  }

  /**
   * Path part for operation searchProposalChats
   */
  static readonly SearchProposalChatsPath = '/api/v1/admin/proposal/search';

  /**
   * Search proposal chats
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `searchProposalChats()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  private searchProposalChats$Response(params: {
    body: ProposalChatSearchForm
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<ResponsePageableListDtoProposalChatListFinalDto>> {

    const rb = new RequestBuilder(this.rootUrl, AdminProposalApiService.SearchProposalChatsPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ResponsePageableListDtoProposalChatListFinalDto>;
      })
    );
  }

  /**
   * Search proposal chats
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `searchProposalChats$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  searchProposalChats(params: {
    body: ProposalChatSearchForm
  }, config?: IWrapHTTPConfig
): Observable<ResponsePageableListDtoProposalChatListFinalDto> {

    return this.wrapHTTPQuery(this.searchProposalChats$Response(params, config).pipe(
      map((r: StrictHttpResponse<ResponsePageableListDtoProposalChatListFinalDto>) => r.body as ResponsePageableListDtoProposalChatListFinalDto)
    ), config);
  }

  /**
   * Path part for operation readProposalChats
   */
  static readonly ReadProposalChatsPath = '/api/v1/admin/proposal/read';

  /**
   * Read proposal chats
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `readProposalChats()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  private readProposalChats$Response(params: {
    body: ProposalChatItemsForm
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<EmptyResponse>> {

    const rb = new RequestBuilder(this.rootUrl, AdminProposalApiService.ReadProposalChatsPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<EmptyResponse>;
      })
    );
  }

  /**
   * Read proposal chats
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `readProposalChats$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  readProposalChats(params: {
    body: ProposalChatItemsForm
  }, config?: IWrapHTTPConfig
): Observable<EmptyResponse> {

    return this.wrapHTTPQuery(this.readProposalChats$Response(params, config).pipe(
      map((r: StrictHttpResponse<EmptyResponse>) => r.body as EmptyResponse)
    ), config);
  }

  /**
   * Path part for operation countNewProposals
   */
  static readonly CountNewProposalsPath = '/api/v1/admin/proposal/countUnread';

  /**
   * Count new proposals
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `countNewProposals()` instead.
   *
   * This method doesn't expect any request body.
   */
  private countNewProposals$Response(params?: {
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<ResponseInteger>> {

    const rb = new RequestBuilder(this.rootUrl, AdminProposalApiService.CountNewProposalsPath, 'post');
    if (params) {
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ResponseInteger>;
      })
    );
  }

  /**
   * Count new proposals
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `countNewProposals$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  countNewProposals(params?: {
  }, config?: IWrapHTTPConfig
): Observable<ResponseInteger> {

    return this.wrapHTTPQuery(this.countNewProposals$Response(params, config).pipe(
      map((r: StrictHttpResponse<ResponseInteger>) => r.body as ResponseInteger)
    ), config);
  }

  /**
   * Path part for operation createProposalChats
   */
  static readonly CreateProposalChatsPath = '/api/v1/admin/proposal';

  /**
   * Create proposal chat
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createProposalChats()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  private createProposalChats$Response(params: {
    body: ProposalChatForm
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<ResponseProposalChatItemDto>> {

    const rb = new RequestBuilder(this.rootUrl, AdminProposalApiService.CreateProposalChatsPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ResponseProposalChatItemDto>;
      })
    );
  }

  /**
   * Create proposal chat
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `createProposalChats$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createProposalChats(params: {
    body: ProposalChatForm
  }, config?: IWrapHTTPConfig
): Observable<ResponseProposalChatItemDto> {

    return this.wrapHTTPQuery(this.createProposalChats$Response(params, config).pipe(
      map((r: StrictHttpResponse<ResponseProposalChatItemDto>) => r.body as ResponseProposalChatItemDto)
    ), config);
  }

  /**
   * Path part for operation deleteProposalChats
   */
  static readonly DeleteProposalChatsPath = '/api/v1/admin/proposal';

  /**
   * Delete proposal chats
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteProposalChats()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  private deleteProposalChats$Response(params: {
    body: ProposalChatItemsForm
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<EmptyResponse>> {

    const rb = new RequestBuilder(this.rootUrl, AdminProposalApiService.DeleteProposalChatsPath, 'delete');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<EmptyResponse>;
      })
    );
  }

  /**
   * Delete proposal chats
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `deleteProposalChats$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  deleteProposalChats(params: {
    body: ProposalChatItemsForm
  }, config?: IWrapHTTPConfig
): Observable<EmptyResponse> {

    return this.wrapHTTPQuery(this.deleteProposalChats$Response(params, config).pipe(
      map((r: StrictHttpResponse<EmptyResponse>) => r.body as EmptyResponse)
    ), config);
  }

}
